import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Input } from '@/components/ui/shadcn/input';
import { useTranslations } from '@/hooks/useTranslations';
import { laravelJsonFetch } from '@/lib/laravelJsonFetch';
import { ArrowDown, ArrowUp, Loader2, Plus, RotateCcw, Save, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface Props {
    initial: string[];
    defaults: string[];
}

export default function FolderTemplateCard({ initial, defaults }: Props) {
    const { t } = useTranslations();
    const [folders, setFolders] = useState<string[]>(initial ?? []);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);

    const add = () => {
        const name = draft.trim();
        if (name === '' || folders.includes(name)) {
            setDraft('');
            return;
        }
        setFolders((f) => [...f, name]);
        setDraft('');
    };

    const remove = (index: number) => setFolders((f) => f.filter((_, i) => i !== index));

    const move = (index: number, dir: -1 | 1) => {
        setFolders((f) => {
            const next = [...f];
            const target = index + dir;
            if (target < 0 || target >= next.length) return f;
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
    };

    const save = async () => {
        setSaving(true);
        try {
            const res = await laravelJsonFetch<{ folder_template: string[] }>('/settings/integrations/sharepoint/folder-template', 'POST', {
                folders,
            });
            setFolders(res.folder_template ?? folders);
            toast.success(t('settings.sharepoint.folder_template.saved', 'Folder template saved.'));
        } catch (err) {
            toast.error(
                t('settings.sharepoint.folder_template.save_failed', 'Failed to save template: {error}').replace('{error}', (err as Error).message),
            );
        } finally {
            setSaving(false);
        }
    };

    const reset = () => {
        if (!window.confirm(t('settings.sharepoint.folder_template.reset_confirm', 'Reset the folder template to the default list?'))) {
            return;
        }
        setFolders(defaults ?? []);
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">{t('settings.sharepoint.folder_template.card_title', 'Project folder template')}</CardTitle>
                <CardDescription>
                    {t(
                        'settings.sharepoint.folder_template.card_description',
                        'Subfolders automatically created inside every new project folder in SharePoint.',
                    )}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Add row */}
                <div className="flex gap-2">
                    <Input
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                add();
                            }
                        }}
                        placeholder={t('settings.sharepoint.folder_template.add_placeholder', 'New subfolder name')}
                    />
                    <Button type="button" variant="outline" onClick={add} className="gap-2">
                        <Plus className="h-4 w-4" />
                        {t('settings.sharepoint.folder_template.add', 'Add')}
                    </Button>
                </div>

                {/* List */}
                {folders.length === 0 ? (
                    <p className="text-muted-foreground rounded-md border border-dashed p-4 text-center text-sm">
                        {t('settings.sharepoint.folder_template.empty', 'No subfolders defined.')}
                    </p>
                ) : (
                    <ul className="divide-y rounded-md border">
                        {folders.map((name, i) => (
                            <li key={`${name}-${i}`} className="flex items-center gap-2 p-2">
                                <span className="text-muted-foreground w-6 text-right text-xs">{i + 1}</span>
                                <span className="flex-1 truncate text-sm">{name}</span>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => move(i, -1)}
                                    disabled={i === 0}
                                    aria-label={t('settings.sharepoint.folder_template.move_up', 'Move up')}
                                >
                                    <ArrowUp className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => move(i, 1)}
                                    disabled={i === folders.length - 1}
                                    aria-label={t('settings.sharepoint.folder_template.move_down', 'Move down')}
                                >
                                    <ArrowDown className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="text-destructive hover:text-destructive h-7 w-7"
                                    onClick={() => remove(i)}
                                    aria-label={t('settings.sharepoint.folder_template.remove', 'Remove')}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </li>
                        ))}
                    </ul>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                    <Button type="button" onClick={() => void save()} disabled={saving} className="gap-2">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {saving
                            ? t('settings.sharepoint.folder_template.saving', 'Saving…')
                            : t('settings.sharepoint.folder_template.save', 'Save template')}
                    </Button>
                    <Button type="button" variant="outline" onClick={reset} className="gap-2">
                        <RotateCcw className="h-4 w-4" />
                        {t('settings.sharepoint.folder_template.reset', 'Reset to default')}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}
